import {Box, Dialog, DialogContent, useTheme, Theme} from '@mui/material';
import LFCIconButton from '_components/inputs/LFCIconButton';
import LFCDialogTitle from '_components/feedback/LFCDialogTitle';
import LFCDataGrid from '_components/datagrid/LFCDataGrid';
import {useState} from 'react';
import {fmtYYYYMMDDHHmmss} from '_logics/LFCFormatUtil';
import {green, red} from '@mui/material/colors';
import {GridRenderCellParams} from '@mui/x-data-grid-pro';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {saveAsCrossOriginCaptureImage} from '_components/common/ScreenCapture';
import {makeStyles} from 'tss-react/mui';
import LFCS3Image from '_components/LFCS3Image';

// スタイル定義
const useStyles = makeStyles()((theme: Theme) => ({
  captureButton: {
    position: 'absolute',
    right: theme.spacing(5.5), // 約45pxに相当
    top: theme.spacing(1)
  }
}));

/**
 * 引数
 */
interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  imgPath: string;
  //後方互換維持
  imageData?: {
    lq_time: string;
    serial: string;
    work: string;
    map: string;
    judge: number;
  };
}

/**
 * 画像表示ダイアログ
 * @param props
 * @returns
 */
const ImageDialog = (props: Props) => {
  const {classes} = useStyles(); // スタイルフックを使用

  const [isImageLoaded, setIsImageLoaded] = useState(true);

  // テーマから背景色を取得
  const theme = useTheme();
  const backgroundColor = theme.palette.background.paper;

  const handleImageError = () => {
    setIsImageLoaded(false);
  };

  // ユニークなIDを生成
  const dialogId = 'map-image-dialog';

  // キャプチャ保存ボタンのクリックハンドラ
  const handleCaptureClick = async () => {
    // キャプチャの実行（テーマの背景色を指定）
    await saveAsCrossOriginCaptureImage(dialogId, 'MapImage', backgroundColor);
  };

  // 判定表示カラー設定用
  const renderJudgeCell = (params: GridRenderCellParams) => {
    if (params.value === '〇') {
      return <span style={{color: green[500], fontWeight: 'bold'}}>〇</span>;
    } else {
      return <span style={{color: red[500], fontWeight: 'bold'}}>✖</span>;
    }
  };

  // データグリッド用カラム定義
  const columns = [
    {
      field: 'time',
      headerName: '日時',
      width: 180
    },
    {
      field: 'serial',
      headerName: 'シリアルNo',
      width: 300
    },
    {
      field: 'work',
      headerName: '機種',
      width: 200
    },
    {
      field: 'map',
      headerName: '部位',
      width: 120
    },
    {
      field: 'judge',
      headerName: '部位別判定',
      width: 100,
      align: 'center' as const,
      renderCell: renderJudgeCell
    }
  ];

  // データグリッド用の行データ
  const rows = props.imageData
    ? [
        {
          id: 1,
          time: fmtYYYYMMDDHHmmss(props.imageData.lq_time),
          serial: props.imageData.serial,
          work: props.imageData.work,
          map: props.imageData.map,
          judge: props.imageData.judge === 0 ? '〇' : '✖'
        }
      ]
    : [];

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth={false}>
      <div id={dialogId}>
        {isImageLoaded && (
          <LFCDialogTitle onClose={props.onClose}>
            {props.title}
            <LFCIconButton onClick={() => handleCaptureClick()} className={classes.captureButton}>
              <PhotoCameraIcon />
            </LFCIconButton>
          </LFCDialogTitle>
        )}
        <DialogContent>
          <Box>
            <LFCS3Image
              src={props.imgPath}
              alt=""
              style={{
                objectFit: 'contain',
                width: '65vw'
              }}
              draggable
              onError={handleImageError}
            />
          </Box>
        </DialogContent>
        {props.imageData && (
          <LFCDataGrid
            columns={columns}
            rows={rows}
            columnOrder={[
              {name: 'time', order: 0},
              {name: 'serial', order: 1},
              {name: 'work', order: 2},
              {name: 'map', order: 3},
              {name: 'judge', order: 4}
            ]}
            hideFooter
            height="110px"
            autoHeight={true}
            pagination={false}
          />
        )}
      </div>
    </Dialog>
  );
};

export default ImageDialog;
